/** `snippets/explore-experience-expanded.liquid` — `<explore-experience-expanded>`:
 *  mobile-only full-screen takeover for the Explore carousel's Experience cards.
 *
 *  Opened by `explore-carousel.js` (`open(index)`) when a card's `+` toggle is
 *  tapped on mobile. Each Experience block is a full-screen slide; the bottom
 *  arrows move between slides (bounded — disabled at the ends) and the top-right
 *  button closes the overlay. On close it dispatches `experience-expanded:close`
 *  with the last-viewed index so the carousel can sync its rail.
 */

const MOBILE_QUERY = '(max-width: 768px)'

const TRACK_SELECTOR = '[data-experience-expanded-track]'
const SLIDE_SELECTOR = '[data-experience-expanded-slide]'
const CLOSE_SELECTOR = '[data-experience-expanded-close]'
const PREV_SELECTOR = '[data-experience-expanded-prev]'
const NEXT_SELECTOR = '[data-experience-expanded-next]'

class ExploreExperienceExpanded extends HTMLElement {
  #abort
  #track
  #slides = []
  #prev
  #next
  #index = 0
  #isOpen = false
  #lastFocused = null
  #mql
  #closeTimer = 0

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#track = this.querySelector(TRACK_SELECTOR)
    this.#slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR))
    this.#prev = this.querySelector(PREV_SELECTOR)
    this.#next = this.querySelector(NEXT_SELECTOR)

    this.querySelector(CLOSE_SELECTOR)?.addEventListener('click', () => this.close(), { signal })
    this.#prev?.addEventListener('click', () => this.#go(-1), { signal })
    this.#next?.addEventListener('click', () => this.#go(1), { signal })

    document.addEventListener('keydown', this.#onKeydown, { signal })

    this.#mql = globalThis.matchMedia?.(MOBILE_QUERY)
    this.#mql?.addEventListener('change', this.#onMediaChange, { signal })

    globalThis.addEventListener('resize', this.#onResize, { signal })
  }

  disconnectedCallback() {
    this.#abort?.abort()
    clearTimeout(this.#closeTimer)
    if (this.#isOpen) this.#unlockScroll()
  }

  get count() {
    return this.#slides.length
  }

  open(index = 0) {
    if (!this.count) return
    clearTimeout(this.#closeTimer)
    this.#lastFocused = document.activeElement

    // Reveal first so the slides have a measurable width, then jump (no
    // animation) to the requested index before fading the overlay in.
    this.removeAttribute('hidden')
    this.#goTo(index, { animate: false })

    // Force a reflow so the freshly-shown element animates its opacity in.
    this.getBoundingClientRect()
    this.#isOpen = true
    this.classList.add('is-open')
    this.#lockScroll()

    this.querySelector(CLOSE_SELECTOR)?.focus({ preventScroll: true })
  }

  close() {
    if (!this.#isOpen) return
    this.#isOpen = false
    this.classList.remove('is-open')
    this.#unlockScroll()

    const finish = () => {
      clearTimeout(this.#closeTimer)
      this.setAttribute('hidden', '')
    }
    // Hide after the fade-out; fall back to a timer if transitionend is missed.
    this.addEventListener('transitionend', (e) => {
      if (e.target === this && e.propertyName === 'opacity') finish()
    }, { once: true })
    this.#closeTimer = globalThis.setTimeout(finish, 400)

    this.dispatchEvent(
      new CustomEvent('experience-expanded:close', {
        bubbles: true,
        detail: { index: this.#index },
      })
    )

    const target = this.#lastFocused
    this.#lastFocused = null
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true })
  }

  #go(direction) {
    this.#goTo(this.#index + direction)
  }

  #goTo(index, { animate = true } = {}) {
    const clamped = Math.max(0, Math.min(this.count - 1, index))
    this.#index = clamped

    if (this.#track) {
      // Translate by the measured slide width in px. A percentage would be
      // relative to the track's own (multi-slide) width, which makes prev/next
      // skip several slides at once.
      const slideWidth = this.#slides[0]?.getBoundingClientRect().width || this.offsetWidth
      if (!animate) this.#track.style.transition = 'none'
      this.#track.style.transform = `translateX(-${clamped * slideWidth}px)`
      if (!animate) {
        this.#track.getBoundingClientRect()
        this.#track.style.transition = ''
      }
    }

    if (this.#prev) this.#prev.disabled = clamped <= 0
    if (this.#next) this.#next.disabled = clamped >= this.count - 1
  }

  #onKeydown = (event) => {
    if (!this.#isOpen) return
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    } else if (event.key === 'ArrowLeft') {
      this.#go(-1)
    } else if (event.key === 'ArrowRight') {
      this.#go(1)
    }
  }

  #onMediaChange = (event) => {
    if (!event.matches && this.#isOpen) this.close()
  }

  // Re-pin the track to the active slide after a viewport/orientation change,
  // since the transform offset is measured in pixels.
  #onResize = () => {
    if (this.#isOpen) this.#goTo(this.#index, { animate: false })
  }

  #lockScroll() {
    this.dataset.prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  #unlockScroll() {
    document.body.style.overflow = this.dataset.prevOverflow ?? ''
    delete this.dataset.prevOverflow
  }
}

if (!customElements.get('explore-experience-expanded')) {
  customElements.define('explore-experience-expanded', ExploreExperienceExpanded)
}
